import { Chip, Tabs } from '@lune-fe/lune-ui-lib'
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined'
import CloudDownloadOutlined from '@mui/icons-material/CloudDownloadOutlined'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image, { StaticImageData } from 'next/image'
import { ReactNode, useMemo } from 'react'

import QuoteCard from '@/components/QuoteCard'
import TwoColumnLayout, { ColumnWrapOrder } from '@/components/TwoColumnLayout'

const chipFunctions = {
    climateProjects: (background: string) => {
        return (
            <Chip
                sx={{ maxWidth: 'max-content', background }}
                icon={<CloudDownloadOutlined sx={{ width: '20px', height: '20px' }} />}
                label={'Climate projects'}
            />
        )
    },
    emissionCalculations: (background: string) => {
        return (
            <Chip
                sx={{ maxWidth: 'max-content', background }}
                icon={<CalculateOutlinedIcon sx={{ width: '20px', height: '20px' }} />}
                label={'Emission calculations'}
            />
        )
    },
}

export type TabDetails<T> = {
    name: T
    logo: StaticImageData
    description: ReactNode
    quoteImage: StaticImageData
    quoteName: string
    quoteAvatar: StaticImageData
    quoteTitle: string
    quoteText: ReactNode
    chipColor: string
    chips: (keyof typeof chipFunctions)[]
}

const TwoColumnLayoutWithTabs = <T extends string>({
    value,
    onChange,
    values,
}: {
    value: T
    onChange: (value: T) => void
    values: Record<T, TabDetails<T>>
}) => {
    const tabs = useMemo(() => {
        const tabs = []

        for (const property in values) {
            const data = values[property]
            tabs.push(data)
        }

        return tabs
    }, [values])

    const {
        name,
        description,
        quoteImage,
        quoteName,
        quoteAvatar,
        quoteTitle,
        quoteText,
        chipColor,
        chips,
    } = values[value]

    return (
        <Box>
            <TwoColumnLayout
                leftCol={6}
                rightCol={5}
                columnWrapOrderOnMobile={ColumnWrapOrder.REVERSE}
            >
                <Box>
                    <Tabs
                        // We need ts ignore here because onChange in ui-lib is typed wrong
                        /* @ts-ignore: ... */
                        onChange={(a: number) => {
                            tabs[a] && onChange(tabs[a].name)
                        }}
                        sx={{
                            ml: -1,
                            '.MuiTab-root': {
                                marginRight: '16px !important',
                            },
                            '.MuiTab-root:not(.Mui-selected) img': {
                                opacity: 0.4,
                            },
                            '.MuiTabs-indicator': {
                                bottom: '8px !important',
                            },
                        }}
                    >
                        {tabs.map((t, i) => (
                            <Tabs.Tab
                                key={i}
                                label={
                                    <Image
                                        height={16}
                                        key={t.name}
                                        src={t.logo}
                                        alt={`${t.name} logo`}
                                    />
                                }
                            />
                        ))}
                    </Tabs>
                    <Text
                        variant={`body1`}
                        sx={{
                            fontSize: {
                                xs: '18px',
                                sm: '18px',
                                md: '18px',
                                lg: '24px',
                            },
                            lineHeight: {
                                xs: '21.6px',
                                sm: '21.6px',
                                md: '21.6px',
                                lg: '28.8px',
                            },
                            py: {
                                xs: 6,
                            },
                        }}
                    >
                        {description}
                    </Text>
                    <Box
                        sx={{
                            display: `flex`,
                            flexDirection: `column`,
                            gap: 1,
                        }}
                    >
                        {chips.map((chipType, index) => (
                            <div key={`c-${index}`}>{chipFunctions[chipType](chipColor)}</div>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ display: `flex` }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '0',
                            paddingBottom: `150.637%`,
                            position: 'relative',
                            borderRadius: `32px`,
                            overflow: 'hidden',
                            img: {
                                position: 'absolute',
                                left: '-50%',
                                right: '-50%',
                                top: 0,
                                bottom: 0,
                                width: 'auto',
                                height: '100%',
                                margin: 'auto',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                position: `absolute`,
                                zIndex: 1,
                            }}
                        >
                            <QuoteCard
                                quoteName={quoteName}
                                quoteTitle={quoteTitle}
                                quoteAvatar={quoteAvatar}
                                quoteText={quoteText}
                            />
                        </Box>
                        <Image src={quoteImage} alt={`${name} peaceful background`} />
                    </Box>
                </Box>
            </TwoColumnLayout>
        </Box>
    )
}

export default TwoColumnLayoutWithTabs
